import { EcmrCard } from '@/components/ecmr/EcmrCard';
import { useSession } from '@/api/session';
import { OrderDocumentsCard, StatusHistoryCard } from '@/components/orders/OrderDocumentsCard';
import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Copy, Pencil, Truck } from 'lucide-react';
import type { OrderStatus } from '@kmdx/contracts';
import { t } from '@/i18n';
import { formatKg, formatMinor } from '@/lib/format';
import { useOrder, useOrderTransition } from '@/api/hooks/orders';
import {
  ORDER_TRANSITION_LABEL,
  ORDER_TRANSITION_NEEDS_REASON,
  allowedOrderTransitions,
} from '@/domain/orderStatus';
import { Badge, StatusPill } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, Stat } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Textarea } from '@/components/ui/Field';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { StopTimeline } from '@/components/orders/StopTimeline';
import { AssignDialog } from '@/components/dispatch/AssignDialog';
import { toast } from '@/stores/toast';

export function OrderDetailPage() {
  const { orderId } = useParams({ from: '/orders/$orderId' });
  const { data: order, isLoading, isError, refetch } = useOrder(orderId);
  const transition = useOrderTransition(orderId);
  const role = useSession((st) => st.user?.role);
  const canIssue = role === 'owner' || role === 'accountant';
  const [assignOpen, setAssignOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);
  const [reason, setReason] = useState('');

  const transitions = useMemo(
    () => (order ? allowedOrderTransitions(order.status, order.allowedTransitions) : []),
    [order],
  );

  if (isLoading) return <LoadingBlock />;
  if (isError || !order) return <ErrorState onRetry={() => void refetch()} />;

  const runTransition = (status: OrderStatus) => {
    if (ORDER_TRANSITION_NEEDS_REASON.includes(status)) {
      setPendingStatus(status);
      return;
    }
    transition.mutate({ status }, { onSuccess: () => toast.ok(t.orders.statusChanged) });
  };

  const confirmWithReason = () => {
    if (!pendingStatus) return;
    transition.mutate(
      { status: pendingStatus, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          toast.ok(t.orders.statusChanged);
          setPendingStatus(null);
          setReason('');
        },
      },
    );
  };

  const total = order.agreedPriceMinor + order.surchargesMinor;
  /** Живият курс на заявката — отказаните не се броят. */
  const liveTrip = (order.trips ?? []).find((trip) => trip.status !== 'cancelled') ?? null;

  /*
   * API-то не връща всички списъци за всяка заявка (документите и събитията
   * идват само когато има такива). Липсващ списък не бива да сваля целия
   * екран — на телефон „нещо се обърка" е задънена улица, без конзола и без
   * F5 под ръка.
   */
  const stops = order.stops ?? [];

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-lg font-bold tracking-tight">{order.number}</h1>
            <StatusPill status={order.status} />
            {order.isHardDeadline ? <Badge tone="warn">{t.orders.hardDeadline}</Badge> : null}
            {order.isAdr ? (
              <Badge tone={order.adrPoints && order.adrPoints > 1000 ? 'danger' : 'warn'}>
                ADR {order.adrPoints ?? '?'} т.
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-ink-dim">
            {order.partnerName}
            {order.customerRef ? ` · ${t.orders.customerRef}: ${order.customerRef}` : ''}
          </p>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            icon={<Copy className="size-3.5" />}
            onClick={() => {
              void navigator.clipboard.writeText(order.publicRef);
              toast.ok(t.common.copied, order.publicRef);
            }}
          >
            {t.orders.publicRef}
          </Button>
          <Link to="/orders/$orderId/edit" params={{ orderId }}>
            <Button size="sm" variant="secondary" icon={<Pencil className="size-3.5" />}>
              {t.common.edit}
            </Button>
          </Link>
          {liveTrip ? (
            <Link to="/trips/$tripId" params={{ tripId: liveTrip.tripId }}>
              <Button size="sm" variant="secondary" icon={<Truck className="size-3.5" />}>
                {liveTrip.number}
              </Button>
            </Link>
          ) : (
            <Button
              size="sm"
              variant="primary"
              icon={<Truck className="size-3.5" />}
              onClick={() => setAssignOpen(true)}
            >
              {t.orders.assignVehicle}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader title={t.orders.timeline} />
            <CardBody>
              <StopTimeline stops={stops} />
            </CardBody>
          </Card>

          <EcmrCard orderId={order.id} />

          <OrderDocumentsCard orderId={order.id} docs={order.docs} canIssue={canIssue} />

          <StatusHistoryCard orderId={order.id} />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title={t.orders.transitions} />
            <CardBody className="space-y-2">
              {transitions.length === 0 ? (
                <p className="text-xs text-ink-dim">{t.orders.noTransitions}</p>
              ) : (
                transitions.map((status) => (
                  <Button
                    key={status}
                    className="w-full justify-start"
                    variant={status === 'cancelled' ? 'danger' : 'secondary'}
                    loading={transition.isPending}
                    onClick={() => runTransition(status)}
                  >
                    {ORDER_TRANSITION_LABEL[status]}
                  </Button>
                ))
              )}
              {order.status === 'documents_pending' ? (
                <p className="text-[11px] text-warn">
                  Спедиторът не плаща без подписана ЧМР — заявката не минава към фактуриране, докато
                  задължителните документи липсват.
                </p>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t.orders.price} />
            <CardBody className="grid grid-cols-2 gap-4">
              <Stat
                label="Договорена цена"
                value={formatMinor(order.agreedPriceMinor, order.ccy)}
              />
              <Stat label="Начисления" value={formatMinor(order.surchargesMinor, order.ccy)} />
              <Stat label={t.common.total} value={formatMinor(total, order.ccy)} tone="accent" />
              {order.declaredValueMinor ? (
                <Stat
                  label={t.orders.declaredValue}
                  value={formatMinor(order.declaredValueMinor, order.declaredValueCcy ?? order.ccy)}
                  hint="Покритието по чл. 23 ЧМР е 8.33 SDR/кг"
                />
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t.orders.goods} />
            <CardBody className="space-y-2 text-sm">
              <p className="text-ink-dim">{order.goodsDescription ?? '—'}</p>
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <Detail label={t.orders.weight} value={formatKg(order.totalWeightKg)} />
                <Detail
                  label="Обем"
                  value={order.volumeM3 != null ? `${order.volumeM3.toLocaleString('bg-BG')} m³` : '—'}
                />
                <Detail label="Колети" value={order.packagesCount?.toString() ?? '—'} />
                <Detail
                  label={t.orders.tailLift}
                  value={order.requiresTailLift ? t.common.yes : t.common.no}
                />

              </dl>
              {order.notes ? <p className="text-xs text-ink-dim">Бележки: {order.notes}</p> : null}
              {order.internalNotes ? (
                <p className="rounded border border-line bg-raised px-2 py-1 text-xs text-ink-dim">
                  {t.orders.internalNotes}: {order.internalNotes}
                </p>
              ) : null}
            </CardBody>
          </Card>
        </div>
      </div>

      <AssignDialog
        open={assignOpen}
        orderId={order.id}
        orderNumber={order.number}
        onClose={() => setAssignOpen(false)}
      />

      <Dialog
        open={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        title={pendingStatus ? ORDER_TRANSITION_LABEL[pendingStatus] : ''}
        description="Причината се записва в заявката и се вижда в справките."
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingStatus(null)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={confirmWithReason} loading={transition.isPending}>
              {t.common.confirm}
            </Button>
          </>
        }
      >
        <Field label="Причина">
          <Textarea data-autofocus value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}
